import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Users, Heart, Gift, Building2, Eye, Edit, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import type { User } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAdminUsers, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useState } from "react";
import { ProviderManagement } from "@/components/ProviderManagement";
import { PageAbout } from "@/components/page-about";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Admin() {
  usePageTitle("Administration", "Administrative tools and oversight for the HealthWallet platform.");
  const { toast } = useToast();
  const [editUser, setEditUser] = useState<User | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", role: "", language: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const { data: users = [], isLoading: usersLoading } = useAdminUsers();

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, string | null> }) => {
      return apiRequest("PATCH", `/api/admin/users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminUsers });
      toast({ title: "User Updated", description: "User details have been saved successfully." });
      setEditUser(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const openEditDialog = (user: User) => {
    setEditForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "patient",
      language: user.language || "en",
    });
    setEditUser(user);
  };

  const handleEditSubmit = () => {
    if (!editUser) return;
    const payload: Record<string, string | null> = {
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      email: editForm.email,
      role: editForm.role,
      language: editForm.language,
    };
    updateUserMutation.mutate({ id: editUser.id, data: payload });
  };

  const getLanguageLabel = (lang: string) => {
    const labels: Record<string, string> = { en: "English", es: "Spanish", so: "Somali", hmn: "Hmong" };
    return labels[lang] || lang;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return <Badge variant="destructive">{role}</Badge>;
      case "patient":
        return <Badge variant="default">{role}</Badge>;
      case "supporter":
        return <Badge variant="secondary">{role}</Badge>;
      case "hospital_partner_admin":
        return <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">hospital partner</Badge>;
      case "employer_admin":
        return <Badge variant="outline" className="border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400">employer admin</Badge>;
      case "driver":
        return <Badge variant="outline" className="border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">{role}</Badge>;
      case "dispatcher":
        return <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">{role}</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (u.firstName || "").toLowerCase().includes(q) ||
      (u.lastName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safePage - 1) * perPage, safePage * perPage);

  const usersByRole = {
    patient: users.filter(u => u.role === "patient").length,
    supporter: users.filter(u => u.role === "supporter").length,
    partners: users.filter(u => u.role === "hospital_partner_admin" || u.role === "employer_admin").length,
  };

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageAbout
        title="Administration"
        description="Manage user accounts, assign roles, and healthcare providers. Control who has access to what features within the platform."
        features={[
          "View and manage all user accounts in the system",
          "Assign roles: Admin, Patient, Supporter, Hospital Partner Admin, Employer Admin",
          "Edit user profiles including name, email, role, and language preference",
          "Manage all healthcare providers and hospital partnerships in one place"
        ]}
        tips={[
          "Use the search bar to quickly find users by name, email, or role",
          "Hospital Partner Admin accounts are linked via invitations",
          "Providers with active hospital partnerships show a 'Partner' badge with a link to their portal"
        ]}
      />
      
      <div>
        <h1 className="text-3xl font-semibold">Administration</h1>
        <p className="text-muted-foreground mt-1">Manage users, roles, and healthcare providers</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">
            <Building2 className="h-4 w-4 mr-2" />
            Providers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Card data-testid="card-total-users">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{users.length}</div>
              </CardContent>
            </Card>

            <Card data-testid="card-patients">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Patients</CardTitle>
                <Heart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{usersByRole.patient}</div>
                <p className="text-xs text-muted-foreground mt-1">Patient accounts</p>
              </CardContent>
            </Card>

            <Card data-testid="card-supporters">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Supporters</CardTitle>
                <Gift className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{usersByRole.supporter}</div>
                <p className="text-xs text-muted-foreground mt-1">Supporter accounts</p>
              </CardContent>
            </Card>

          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
              <CardTitle>User Management</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-9 w-60"
                    data-testid="input-search-users"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <Table data-testid="users-table">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">Language</TableHead>
                      <TableHead className="font-semibold">Joined</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          {searchQuery ? "No users match your search" : "No users found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                          <TableCell className="font-medium">
                            {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName || user.lastName || "-"}
                          </TableCell>
                          <TableCell className="text-sm">{user.email || "No email"}</TableCell>
                          <TableCell>{getRoleBadge(user.role || "patient")}</TableCell>
                          <TableCell className="text-sm">{getLanguageLabel(user.language || "en")}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {user.createdAt ? format(new Date(user.createdAt), "MMM d, yyyy") : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setViewUser(user)}
                                data-testid={`button-view-user-${user.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(user)}
                                data-testid={`button-edit-user-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between gap-2 mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredUsers.length === 0 ? 0 : (safePage - 1) * perPage + 1}-{Math.min(safePage * perPage, filteredUsers.length)} of {filteredUsers.length} users
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    data-testid="button-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {safePage} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    data-testid="button-next-page"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers">
          <ProviderManagement />
        </TabsContent>

      </Tabs>

      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent data-testid="dialog-edit-user">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title-edit-user">Edit User</DialogTitle>
            <DialogDescription>Update user details for {editUser?.username}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editForm.firstName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                  data-testid="input-edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editForm.lastName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                  data-testid="input-edit-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="input-edit-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(val) => setEditForm(prev => ({ ...prev, role: val }))}>
                  <SelectTrigger data-testid="select-edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="supporter">Supporter</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="hospital_partner_admin">Hospital Partner Admin</SelectItem>
                    <SelectItem value="employer_admin">Employer Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={editForm.language} onValueChange={(val) => setEditForm(prev => ({ ...prev, language: val }))}>
                  <SelectTrigger data-testid="select-edit-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="so">Somali</SelectItem>
                    <SelectItem value="hmn">Hmong</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} data-testid="button-cancel-edit-user">Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              disabled={updateUserMutation.isPending || !editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()}
              data-testid="button-save-edit-user"
            >
              {updateUserMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewUser} onOpenChange={(open) => { if (!open) setViewUser(null); }}>
        <DialogContent data-testid="dialog-view-user">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title-view-user">User Details</DialogTitle>
            <DialogDescription>Viewing profile for {viewUser?.username}</DialogDescription>
          </DialogHeader>
          {viewUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">First Name</p>
                  <p className="font-medium" data-testid="view-first-name">{viewUser.firstName || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Last Name</p>
                  <p className="font-medium" data-testid="view-last-name">{viewUser.lastName || "-"}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium" data-testid="view-email">{viewUser.email || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Username</p>
                <p className="font-medium font-mono" data-testid="view-username">{viewUser.username || "-"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Role</p>
                  <div data-testid="view-role">{getRoleBadge(viewUser.role || "patient")}</div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Language</p>
                  <p className="font-medium" data-testid="view-language">{getLanguageLabel(viewUser.language || "en")}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="font-mono text-xs" data-testid="view-user-id">{viewUser.id}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Joined</p>
                  <p className="text-sm" data-testid="view-created-at">
                    {viewUser.createdAt ? format(new Date(viewUser.createdAt), "MMM d, yyyy 'at' h:mm a") : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUser(null)} data-testid="button-close-view-user">Close</Button>
            <Button onClick={() => { if (viewUser) { openEditDialog(viewUser); setViewUser(null); } }} data-testid="button-edit-from-view">
              <Edit className="w-4 h-4 mr-2" />
              Edit User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
